import React, {FC, useRef, useState} from "react";
import FfairModal from "view/components/modal/FfairModal";
import FfairPaper from "view/components/FfairPaper";
import {Box, Button, Typography} from "@material-ui/core";
import PhotoSizeSelectLargeIcon from "@material-ui/icons/PhotoSizeSelectLarge";
import CropIcon from "@material-ui/icons/Crop";
import FlexBox from "view/components/Layout/FlexBox";
import Alert from "view/components/Alert";
import {loadImageAsDataUri} from "tools/richText/loadImageAsDataUri";
import useStyles from "./styles";
import CropImage from "./components/CropImage";
import {Image} from "../../types";
import ResizeImage from "./components/ResizeImage";

type Props = {
  image: Image;
  onClose: () => void;
  onAddImage: (src: string, scale: number) => Promise<void>;
};
enum ChangeType {
  Resize = "resize",
  Crop = "crop",
}

const EditImageModal: FC<Props> = ({image, onClose, onAddImage}) => {
  const classes = useStyles();
  const imgRef = useRef<HTMLImageElement | null>(null);

  // What will be saved: the node's own src to begin with, replaced by a new
  // `data:` URI only once a crop is actually applied. Keeping it untouched
  // otherwise is what lets `handleAddImage` tell a resize-only edit apart and
  // skip re-uploading a file that is already on storage.
  const [imageSource, setImageSource] = useState<string>(image.dataString);
  // Pixels for the crop step, inlined on demand (see `openCrop`). Held apart
  // from `imageSource` so backing out of cropping doesn't turn the edit into
  // a re-upload.
  const [cropSource, setCropSource] = useState<string | null>(
    image.dataString.startsWith("data:") ? image.dataString : null
  );
  const [isLoadingCropSource, setIsLoadingCropSource] = useState(false);
  const [error, setError] = useState("");
  const [scale, setScale] = useState<number>(image.scale);

  const [changeType, setChangeType] = useState<null | ChangeType>(null);
  const clearChangeType = () => setChangeType(null);

  const openCrop = async () => {
    setError("");
    if (cropSource) {
      setChangeType(ChangeType.Crop);
      return;
    }
    setIsLoadingCropSource(true);
    try {
      const dataUri = await loadImageAsDataUri(imageSource);
      setCropSource(dataUri);
      setChangeType(ChangeType.Crop);
    } catch (e) {
      setError(
        "Couldn't load this image for cropping. Resizing still works, and you can re-insert the image to crop it."
      );
      // eslint-disable-next-line no-console
      console.error(
        "EditImageModal: failed to inline the image for cropping",
        e
      );
    } finally {
      setIsLoadingCropSource(false);
    }
  };

  const ImageContent = () => {
    switch (changeType) {
      case ChangeType.Crop:
        return (
          <CropImage
            imageSource={cropSource ?? imageSource}
            extension={image.extension}
            onSave={(src) => {
              // A crop produces new pixels — this is the one edit that has to
              // go through the upload adapter on save.
              setImageSource(src);
              setCropSource(src);
              setChangeType(null);
            }}
            onCancel={clearChangeType}
          />
        );
      case ChangeType.Resize:
        return (
          <ResizeImage
            imageSource={imageSource}
            defaultScale={scale}
            onSave={(scale) => {
              setScale(scale);
              setChangeType(null);
            }}
            onCancel={clearChangeType}
          />
        );
      default:
        return (
          <img
            ref={imgRef}
            src={imageSource}
            className={classes.image}
            alt=""
            style={{
              maxWidth: "100%",
              transformOrigin: "top left",
              transform: `scale(${scale / 100})`,
            }}
          />
        );
    }
  };
  const getButtonVariant = (type: ChangeType): "contained" | "outlined" =>
    type === changeType ? "contained" : "outlined";

  const submitTitle = image?.element ? "Save" : "Add";
  return (
    <FfairModal open onClose={onClose}>
      <FfairPaper className={classes.root}>
        <Box className={classes.header}>
          <Typography variant="h2" className={classes.title}>
            Edit image
          </Typography>
        </Box>
        <FlexBox mb={3} justifyContent="space-between" alignCenter>
          <FlexBox>
            <Box mr={2}>
              <Button
                color="primary"
                variant={getButtonVariant(ChangeType.Resize)}
                startIcon={<PhotoSizeSelectLargeIcon />}
                onClick={() => setChangeType(ChangeType.Resize)}
                disabled={changeType === ChangeType.Crop}
              >
                RESIZE
              </Button>
            </Box>
            <Button
              color="primary"
              variant={getButtonVariant(ChangeType.Crop)}
              startIcon={<CropIcon />}
              onClick={openCrop}
              disabled={changeType === ChangeType.Resize || isLoadingCropSource}
            >
              CROP
            </Button>
          </FlexBox>
        </FlexBox>
        {error && (
          <Box mb={2}>
            <Alert severity="error" fullWidth>
              {error}
            </Alert>
          </Box>
        )}
        <ImageContent />
        <Box display="flex" justifyContent="flex-end" pt={3}>
          <Box pr={1}>
            <Button color="primary" onClick={onClose}>
              Cancel
            </Button>
          </Box>
          <Button
            color="primary"
            variant="contained"
            disabled={!!changeType}
            onClick={async () => {
              await onAddImage(imageSource, scale);
              onClose();
            }}
          >
            {submitTitle}
          </Button>
        </Box>
      </FfairPaper>
    </FfairModal>
  );
};

export default EditImageModal;
